import { setContext } from '@apollo/client/link/context';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';

import { notifyAuthExpired } from './auth-expiry';
import { resolveInitialLocale } from '../core/providers/resolve-initial-locale';

/**
 * Apollo auth link — injects the Auth0 Bearer token into every outgoing
 * GraphQL request. Requests made before the Auth0Provider has booted (or while
 * the user is logged out) are forwarded without an Authorization header; the
 * server returns an auth error for protected operations, which is the expected
 * behaviour rather than a silent failure.
 *
 * Architecture note: the Apollo client is a module-level singleton, so we
 * can't use Auth0 hooks directly inside the link factory. Instead we install a
 * `tokenGetter` callback from within the React tree (see `useInstallAuthLink`)
 * and the link calls it on each request.
 */
let tokenGetter: (() => Promise<string>) | null = null;

export function setTokenGetter(fn: (() => Promise<string>) | null): void {
  tokenGetter = fn;
}

/**
 * Header carrying the active tenant a multi-tenant user has selected. The server
 * (`ActiveTenantGuard`) validates membership before binding it as the RLS scope.
 */
export const ACTIVE_TENANT_HEADER = 'x-syncanix-tenant';

/**
 * Operations that must NEVER carry the active-tenant header, because each one has
 * to work when the persisted selection is stale, invalid, or points at a
 * workspace the user has lost access to. Attaching a tenant to any of these
 * converts a recoverable bad selection into a hard lockout from the very
 * operation that would resolve it.
 *
 *  - `MyTenants` — the bootstrap that determines the valid tenant set.
 *  - `TermsAcceptance` / `AcceptTerms` (ADR-0497) — the acceptance gate resolves
 *    a PERSON, not a workspace, and both resolvers are `@TenantOptional()`. These
 *    are the strongest case in the list: a user who cannot accept the terms
 *    cannot reach any page of the app, so a stale tenant id in localStorage would
 *    be an unrecoverable lockout rather than a wrong-data bug.
 */
const TENANT_INDEPENDENT_OPERATIONS: ReadonlySet<string> = new Set([
  'MyTenants',
  'TermsAcceptance',
  'AcceptTerms',
]);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

let tenantGetter: (() => string | null) | null = null;

/**
 * Boot-time fallback: the persisted workspace selection. The React getter only
 * installs in a useEffect — AFTER the first render's queries have fired — so a
 * fresh page load would otherwise send its first requests tenant-less and the
 * server would silently bind the personal tenant (wrong-workspace data on
 * first paint; caught live 2026-06-10). Reads the same key `WorkspaceProvider`
 * persists; `tenantHeadersFor`'s uuid check still gates what gets attached.
 */
function readPersistedTenantId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('syncanix:workspace');
  } catch {
    return null;
  }
}

/**
 * Install the getter the link reads to attach `x-syncanix-tenant`. Registered
 * from inside `WorkspaceProvider`'s tree (same indirection as the token getter,
 * because the Apollo client is a module-level singleton).
 */
export function setTenantHeaderGetter(fn: (() => string | null) | null): void {
  tenantGetter = fn;
}

/**
 * Compute the tenant header for an operation. Pure + exported for testing.
 *
 *  - The {@link TENANT_INDEPENDENT_OPERATIONS} never carry the header — each is
 *    an operation that must survive a stale or invalid selection, so attaching a
 *    (possibly stale) active tenant could lock the user out of the very operation
 *    that would fix it.
 *  - Only a uuid-shaped id is sent. The pre-hydration placeholder workspace id
 *    is not a uuid, so nothing is attached until real tenants have loaded —
 *    and the server would reject a non-uuid tenant anyway.
 */
export function tenantHeadersFor(
  operationName: string | undefined,
  tenantId: string | null,
): Record<string, string> {
  if (operationName !== undefined && TENANT_INDEPENDENT_OPERATIONS.has(operationName)) return {};
  if (tenantId !== null && UUID_RE.test(tenantId)) {
    return { [ACTIVE_TENANT_HEADER]: tenantId };
  }
  return {};
}

/**
 * Build the auth headers for a NON-GraphQL API call (e.g. the multipart
 * docs-upload `fetch`), reusing the SAME token + active-tenant mechanism the
 * Apollo `authLink` uses. This keeps the upload's `Authorization: Bearer
 * <token>` and `x-syncanix-tenant` exactly aligned with the GraphQL client —
 * one source of truth for credentials, no second Auth0 wiring.
 *
 * `operationName` lets callers reuse the `tenantHeadersFor` rules; REST callers
 * pass `undefined` (they are never the `MyTenants` bootstrap), so the active
 * tenant is attached whenever a uuid-shaped tenant is selected.
 *
 * Returns headers WITHOUT a token if the user is unauthenticated or a refresh
 * fails — the server then returns a 401, which the caller surfaces, rather than
 * silently swallowing the failure.
 */
export async function getApiAuthHeaders(operationName?: string): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    // Server-localized strings (@CurrentLocale reads Accept-Language) must
    // follow the IN-APP language picker, not the browser default (UGL-103 —
    // e.g. the built-in audience templates). Same resolution order as the
    // I18nProvider (saved preference → browser → EN), re-read per request so a
    // live language switch takes effect immediately. CORS-safelisted header.
    'accept-language': resolveInitialLocale(),
    ...tenantHeadersFor(operationName, tenantGetter?.() ?? readPersistedTenantId()),
  };
  if (!tokenGetter) return headers;
  try {
    const token = await tokenGetter();
    headers.authorization = `Bearer ${token}`;
  } catch {
    // The getter is installed but the refresh threw — the session is dead
    // (expired refresh token). Forward tokenless (the server returns 401 for a
    // protected upload route) AND trigger re-login, mirroring the GraphQL
    // authLink, so an expired session re-authenticates instead of silently
    // failing the action.
    notifyAuthExpired();
  }
  return headers;
}

export const authLink = setContext(
  async (request, prevContext: { headers?: Record<string, string>; tenantId?: string }) => {
    // A per-operation `context: { tenantId }` override beats the installed
    // getter. The getter closes over React state and only re-registers in a
    // useEffect, so a mutation fired in the same tick as a workspace switch
    // (e.g. the onboarding wizard persisting the surface pick for the JUST-
    // created workspace) would otherwise carry the previous workspace's id.
    const headers: Record<string, string> = {
      // The picker locale rides every GraphQL request too (see
      // getApiAuthHeaders) — before the prevContext spread so an explicit
      // per-operation header can still override it.
      'accept-language': resolveInitialLocale(),
      ...(prevContext.headers ?? {}),
      ...tenantHeadersFor(
        request.operationName,
        prevContext.tenantId ?? tenantGetter?.() ?? readPersistedTenantId(),
      ),
    };
    if (!tokenGetter) return { headers };
    try {
      const token = await tokenGetter();
      return { headers: { ...headers, authorization: `Bearer ${token}` } };
    } catch {
      // The getter is installed but `getAccessTokenSilently` threw — the session
      // is genuinely dead (expired refresh token), NOT the pre-boot
      // `!tokenGetter` case above. Forward tokenless so @Public() endpoints
      // still resolve, AND hand off to the auth-expiry handler so a protected
      // page re-authenticates instead of rendering a stuck UNAUTHENTICATED error
      // — the server returns that as a GraphQL error (HTTP 200), which the
      // errorLink only logs, so re-login would otherwise never fire.
      notifyAuthExpired();
      return { headers };
    }
  },
);

/**
 * Install the Auth0 `getAccessTokenSilently` function as the global token
 * getter used by `authLink`. Mount this inside the Auth0Provider tree (it
 * uses `useAuth0` internally).
 *
 * Call site: `AuthLinkInstaller` in `Providers.tsx`, which wraps the Apollo
 * provider so the token getter is available before any GraphQL request fires.
 */
export function useInstallAuthLink(): void {
  const { getAccessTokenSilently } = useAuth0();
  useEffect(() => {
    setTokenGetter(getAccessTokenSilently);
    return () => {
      setTokenGetter(null);
    };
  }, [getAccessTokenSilently]);
}
